import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import {
  FirmaFormState,
  initialRegistrationState,
} from './firma.model';
import { Lote } from 'src/app/models/Lote.model';

@Injectable({
  providedIn: 'root' // Hace que este servicio sea un Singleton en toda la aplicación
})
export class FirmaStateService {
  // 1. BehaviorSubject: Almacena el estado actual. Es privado para controlar las actualizaciones.
  private _firmaState = new BehaviorSubject<FirmaFormState>(initialRegistrationState);
  public readonly firmaState$: Observable<FirmaFormState> = this._firmaState.asObservable();

  // 2. Observable público: Los componentes se suscribirán a este para leer el estado.
  // Usamos .asObservable() para evitar que los componentes llamen a .next() directamente.
  public readonly registrationState$: Observable<FirmaFormState> = this._firmaState.asObservable();

  // --- Selectores (para obtener partes específicas del estado) ---
  // Estos selectores son Observables derivados, optimizados con distinctUntilChanged
  // para emitir solo cuando la parte relevante del estado cambia.

  public readonly currentStep$: Observable<number> = this.registrationState$.pipe(
    map(state => state.currentStep),
    distinctUntilChanged()
  );

  public readonly isComplete$: Observable<boolean> = this.registrationState$.pipe(
    map(state => state.isComplete),
    distinctUntilChanged()
  );

  public readonly lote$: Observable<Lote | null> = this.registrationState$.pipe(
    map(state => state.lote),
    distinctUntilChanged()
  );


  // Selector combinado para obtener todos los datos finales (ejemplo)
  public readonly registrationSummary$: Observable<FirmaFormState> = this.registrationState$.pipe(
    map(state => ({
      lote: state.lote,
      currentStep: state.currentStep,
      isComplete: state.isComplete,
      lote_certificado: state.lote_certificado
    })),
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)) // Para objetos complejos
  );

  // --- Métodos para actualizar el estado (como "mutaciones" inmutables) ---
  // Estos métodos son los únicos que llaman a .next() en _registrationState.
  // Siempre crean un NUEVO objeto de estado para garantizar la inmutabilidad.

  /**
   * Actualiza los datos personales y avanza al siguiente paso.
   * @param data Los datos personales a guardar.
   */
  updateLote(data: Lote): void {
    const currentState = this._firmaState.getValue();
    this._firmaState.next({
      ...currentState, // Copia el estado actual
      lote: { ...data }, // Copia los datos para asegurar inmutabilidad anidada
      currentStep: 2, // Avanza al siguiente paso
    });
  }

  updateLoteCertificado(data: Array<any>): void {
    const currentState = this._firmaState.getValue();
    this._firmaState.next({
      ...currentState, // Copia el estado actual
      lote_certificado: data, // Copia los datos para asegurar inmutabilidad anidada
      currentStep: 4, // Avanza al siguiente paso
    });
  }

  /**
   * Actualiza los datos de contacto y avanza al siguiente paso.
   * @param data Los datos de contacto a guardar.
   */
  /*
  updateContactInfo(data: ContactInfo): void {
    const currentState = this._firmaState.getValue();
    this._firmaState.next({
      ...currentState,
      contactInfo: { ...data },
      currentStep: 3,
    });
    console.log('Estado actualizado (Contact Info):', this._firmaState.getValue());
  }
  */

  /**
   * Retrocede un paso en el formulario.
   */
  goToPreviousStep(): void {
    const currentState = this._firmaState.getValue();
    if (currentState.currentStep > 1) {
      this._firmaState.next({
        ...currentState,
        currentStep: currentState.currentStep - 1,
      });
    }
  }

  /**
   * Restablece el estado del formulario a su valor inicial.
   */
  resetForm(): void {
    this._firmaState.next(initialRegistrationState);
  }

  /**
   * Obtiene el estado actual de forma síncrona (usar con precaución y solo si es necesario).
   */
  getCurrentState(): FirmaFormState {
    return this._firmaState.getValue();
  }

  // Método para actualizar el paso actual desde el componente padre o hijo
  setCurrentStep(step: number): void {
    const currentState = this._firmaState.getValue();
    this._firmaState.next({
      ...currentState,
      currentStep: step,
      isComplete: false // Asegurarse de que no esté completo si se navega a un paso anterior
    });
  }
}